/**
 * Helpers for POST /api/sales (plain JSON) vs legacy show_sales / add_sales shapes.
 */

import { parsePaymentModeFromApi } from "./paymentLinesErpAdapter";
import { readPaymentIdempotencyKeyFromApi } from "./paymentIdempotency";

/** Dispatched after a sale is created so Invoice table can refetch if socket missed. */
export const ERP_SALES_LIST_INVALIDATE_EVENT = "erp:sales-list-invalidate";

export const isSalesApiSuccess = (res) =>
  res &&
  (res.success === true ||
    res.status === true ||
    String(res.status || "").toLowerCase() === "success" ||
    res.message === "radha");

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

export const sumPaymentAmounts = (payments) => {
  if (!Array.isArray(payments)) return 0;
  return payments.reduce(
    (s, p) => s + num(p.amount ?? p.amt ?? p.total_amount, 0),
    0,
  );
};

export const remainingFromGrandAndPayments = (grandTotal, payments) => {
  const g = num(grandTotal, 0);
  const paid = sumPaymentAmounts(payments);
  return Math.max(0, g - paid);
};

/** First sale object from list/show response (handles multiple envelopes). */
export function pickSaleRowFromResponse(res) {
  if (!res?.data) return null;
  if (res.data.sale_details) return res.data.sale_details;
  if (res.data.sale) return res.data.sale;
  if (Array.isArray(res.data) && res.data.length) return res.data[0];
  // Modular show often returns the sale document as `data` itself
  if (
    typeof res.data === "object" &&
    !Array.isArray(res.data) &&
    (res.data.id != null ||
      Array.isArray(res.data.items) ||
      Array.isArray(res.data.sales))
  ) {
    return res.data;
  }
  return null;
}

/**
 * Map Sales.jsx It Rate On (NWt / GWt / Pcs) → API `item_rate_on`.
 * `PER_GRAM` alone does not distinguish net vs gross; we send explicit weight basis.
 */
export function mapItemRateOn(label) {
  const raw = String(label ?? "").trim();
  const u = raw.toUpperCase();
  if (u === "GWT" || raw === "GWt") return "GWT";
  if (u === "NWT" || raw === "NWt") return "NWT";
  if (u === "PCS" || raw === "Pcs") return "PER_PIECE";
  return "NWT";
}

/**
 * Map API `item_rate_on` → Sales.jsx dropdown (NWt / GWt / Pcs).
 * Accepts legacy `PER_GRAM` (treated as net-weight basis).
 */
export function mapApiItemRateOnToUi(apiValue) {
  const u = String(apiValue ?? "").trim().toUpperCase();
  if (u === "GWT" || u === "PER_GRAM_GWT" || u === "GROSS") return "GWt";
  if (u === "NWT" || u === "PER_GRAM_NWT" || u === "NET") return "NWt";
  if (u === "PER_PIECE" || u === "PCS" || u === "PIECE") return "Pcs";
  if (u === "PER_GRAM") return "NWt";
  return "NWt";
}

/**
 * Map Sales.jsx L Type (NWt / GWt / Pcs / Fixed) → API `lbr_type`.
 */
export function mapLbrTypeForApi(lbrType) {
  const raw = String(lbrType ?? "").trim();
  const u = raw.toUpperCase();
  if (u === "FIXED" || u === "FLAT") return "FIXED";
  if (u === "PERCENT" || raw.includes("%")) return "PERCENT";
  if (u === "GWT" || raw === "GWt") return "GWT";
  if (u === "NWT" || raw === "NWt") return "NWT";
  if (u === "PCS" || raw === "Pcs") return "PER_PIECE";
  return "NWT";
}

/** Map API `lbr_type` → Sales.jsx L Type dropdown. */
export function mapApiLbrTypeToUi(apiValue) {
  const u = String(apiValue ?? "").trim().toUpperCase();
  if (u === "GWT" || u === "PER_GRAM_GWT") return "GWt";
  if (u === "NWT" || u === "PER_GRAM" || u === "PER_GRAM_NWT") return "NWt";
  if (u === "PER_PIECE" || u === "PCS") return "Pcs";
  if (u === "FIXED" || u === "FLAT") return "Fixed";
  if (u === "PERCENT") return "Percent";
  return "NWt";
}

/**
 * Convert new modular "show" sale row into legacy `sale_details` shape used by
 * Invoice / EditInvoice (sales[], payments[], voucher_number, etc.).
 */
function enrichSalePayments(payments) {
  if (!Array.isArray(payments)) return [];
  return payments.map((p, i) => {
    const urdDocId = p.urd_document_id ?? p.urdDocumentId ?? null;
    return {
      ...p,
      id: p.id ?? p.transaction_id ?? i + 1,
      urd_document_id: urdDocId,
      urd_doc_number: p.urd_doc_number ?? p.urdDocNumber ?? null,
      old_purchase_id:
        p.old_purchase_id ?? p.oldPurchaseId ?? urdDocId ?? null,
      nwt: p.nwt ?? p.net_weight ?? p.netWeight ?? null,
    };
  });
}

export function newShowRowToLegacySaleDetails(s) {
  if (!s) return null;
  // Already legacy-shaped
  if (Array.isArray(s.sales) && s.voucher_number !== undefined) {
    return {
      ...s,
      payments: enrichSalePayments(s.payments),
      payable_amount: s.payable_amount ?? s.payableAmount ?? 0,
      sale_payable: s.sale_payable ?? s.salePayable ?? s.payables ?? [],
    };
  }
  if (Array.isArray(s.sales) && !s.doc_number) {
    return {
      ...s,
      payments: enrichSalePayments(s.payments),
      payable_amount: s.payable_amount ?? s.payableAmount ?? 0,
      sale_payable: s.sale_payable ?? s.salePayable ?? s.payables ?? [],
    };
  }

  const grandTotal = num(s.grand_total, 0);
  const paymentsIn = Array.isArray(s.payments) ? s.payments : [];
  const paid = sumPaymentAmounts(paymentsIn);
  const remaining = Math.max(0, grandTotal - paid);

  const legacyPayments = paymentsIn.map((p, i) => {
    const ledgerId = p.id ?? p.transaction_id ?? i + 1;
    const idem = readPaymentIdempotencyKeyFromApi(p);
    const payModeUi = parsePaymentModeFromApi(p.payment_mode);
    const bankName = String(p.bank_name || "").trim();
  const urdDocId = p.urd_document_id ?? p.urdDocumentId ?? null;
  const urdDocNumber = p.urd_doc_number ?? p.urdDocNumber ?? null;
    const paymentNwt = p.nwt ?? p.net_weight ?? p.netWeight ?? null;
    return {
      id: ledgerId,
      transaction_id: p.transaction_id ?? p.id,
      idempotency_key: idem || undefined,
      amount: num(p.amount, 0),
      amt: num(p.amount, 0),
      total_amount: num(p.amount, 0),
      pay_mode: payModeUi,
      payment_mode: p.payment_mode,
      bank_account_id: p.bank_account_id,
      bank_name: bankName,
      bank_register_id: p.bank_register_id,
      remarks: p.remarks || "",
      pay_type: p.pay_type ?? p.payType ?? null,
      created_at: p.transaction_date,
      date: p.transaction_date,
      urd_document_id: urdDocId,
      urd_doc_number: urdDocNumber,
      old_purchase_id:
        p.old_purchase_id ?? p.oldPurchaseId ?? urdDocId ?? null,
      nwt: paymentNwt,
      ledger_linked: p.ledger_linked,
    };
  });

  const items = Array.isArray(s.items) ? s.items : [];
  const sales = items.map((it) => ({
    id: it.id,
    product_id: it.product_id,
    // Prefer numeric group id for legacy UIs that match dropdown by `value`
    product_group:
      it.product_group_id != null
        ? it.product_group_id
        : it.product_group_name || it.product_group,
    product_group_id: it.product_group_id,
    product_group_name: it.product_group_name,
    item_category_name: it.item_category_name,
    carat: it.carat,
    design: it.design || "",
    tag_no: it.tag_no || "",
    size: it.size || "",
    pcs: it.pcs ?? 1,
    gwt: it.gwt,
    lwt: it.lwt,
    nwt: it.nwt,
    itrate: it.item_rate ?? it.rate ?? it.itrate,
    itrateon: mapApiItemRateOnToUi(it.item_rate_on ?? it.itrateon),
    itamt: it.item_amount ?? it.itamt,
    totalamt: it.total_amount ?? it.totalamt,
    lbr_percent: it.lbr_percent ?? it.lbr_percentage,
    lbr_rate: it.lbr_rate,
    lbr_amt: it.lbr_amt,
    lbr_type: mapApiLbrTypeToUi(it.lbr_type),
    othamt: it.oth_amt ?? it.othamt,
    huid: it.huid,
    mrp: it.mrp,
    charges: it.charges || [],
    image: it.image,
  }));

  return {
    id: s.id,
    voucher_number: s.doc_number || s.voucher_number,
    date: s.doc_date || s.date,
    created_at: s.created_at || "",
    by_user: resolveSaleByUserId(s),
    by_user_name:
      s.by_user_name ?? s.created_by_name ?? s.byUserName ?? null,
    bill_type_id: s.bill_type_id ?? s.bill_type ?? null,
    bill_type: s.bill_type_id ?? s.bill_type ?? null,
    bill_type_name: s.bill_type_name,
    estimate: s.estimate ?? s.is_estimate ?? s.estimate_flag ?? 0,
    user_id: s.customer_id ?? s.user_id,
    customer_id: s.customer_id,
    name: s.customer_name,
    mobile: s.customer_mobile,
    address: s.customer_address,
    city: s.customer_city,
    state: s.customer_state,
    aadhar_no: s.customer_aadhar_no || "",
    pan_no: s.customer_pan_no || "",
    final_amount: s.total_amount ?? s.final_amount ?? s.grand_total,
    grand_total: s.grand_total,
    discount: s.discount,
    discount_amount: s.discount_amount ?? s.discount,
    gst_percent: s.gst_percent,
    gst_amount: s.tax_amount ?? s.gst_amount,
    GST_number: s.customer_gst_number || s.GST_number,
    sgst: s.sgst,
    cgst: s.cgst,
    igst: s.igst,
    tax_amount: s.tax_amount,
    round_off: s.round_off ?? "0",
    note: s.notes ?? s.note,
    remaining_amount: String(remaining.toFixed(4)),
    payable_amount: s.payable_amount ?? s.payableAmount ?? 0,
    sale_payable: s.sale_payable ?? s.salePayable ?? s.payables ?? [],
    gst_portal: s.gst_portal ?? s.gstPortal ?? null,
    irn: s.irn ?? s.gst_portal?.irn ?? s.gstPortal?.irn ?? null,
    ack_no: s.ack_no ?? s.ackNo ?? s.gst_portal?.ack_no ?? s.gstPortal?.ack_no ?? null,
    ack_date:
      s.ack_date ??
      s.ackDate ??
      s.gst_portal?.ack_date ??
      s.gstPortal?.ack_date ??
      null,
    signed_qr_code:
      s.signed_qr_code ??
      s.signed_qr ??
      s.signedQrCode ??
      s.gst_portal?.signed_qr_code ??
      s.gstPortal?.signed_qr_code ??
      null,
    portal_status:
      s.portal_status ??
      s.einvoice_status ??
      s.gst_portal?.portal_status ??
      s.gstPortal?.portal_status ??
      null,
    status: s.status,
    sales,
    payments: legacyPayments,
    userss:
      s.customer_id != null
        ? {
            id: s.customer_id,
            fullName: s.customer_name,
            name: s.customer_name,
            mobile: s.customer_mobile,
            address: s.customer_address || "",
            city: s.customer_city || "",
            state: s.customer_state || "",
            area: s.customer_area || "",
            email: s.customer_email || "",
            birth_date: s.customer_birth_date,
            anniversary_date: s.customer_anniversary_date,
            company_name: s.customer_company_name,
            company_address: s.customer_company_address,
            GST_number: s.customer_gst_number || s.GST_number,
            aadhar_no: s.customer_aadhar_no,
            pan_no: s.customer_pan_no,
            marital_status: s.customer_marital_status,
            balance: s.customer_balance,
            final_balance: s.customer_final_balance,
            sale_balance: s.customer_sale_balance,
            reference: s.customer_reference,
          }
        : null,
    old_metal_details: s.old_metal_details,
    old_purchase:
      s.old_purchase ??
      s.old_purchase_details ??
      s.old_metal_details ??
      [],
    old_purchase_details: s.old_purchase_details ?? s.old_metal_details,
    ...readSaleBilledTo(s),
  };
}

function readSaleBilledTo(sale) {
  const nested = sale?.billed_to;
  const fromList = Array.isArray(nested) ? nested[0] : null;
  const fromObject = nested && !Array.isArray(nested) ? nested : null;
  const source = fromObject || fromList || {};
  const billedToId =
    sale?.billed_to_id ?? source.customer_id ?? source.id ?? null;

  return {
    billed_to_id: billedToId,
    billedTo: {
      id: billedToId,
      name: source.name || source.fullName || sale?.billed_to_name || "",
      mobile: source.mobile || sale?.billed_to_mobile || "",
      address: source.address || sale?.billed_to_address || "",
      area: source.area || sale?.billed_to_area || "",
    },
  };
}

export function getLegacySaleDetailsFromShowResponse(res) {
  if (!isSalesApiSuccess(res)) return null;
  const row = pickSaleRowFromResponse(res);
  if (!row) return null;
  return newShowRowToLegacySaleDetails(row);
}

/** Pull array of sale rows from various ERP list envelopes. */
export function extractSalesListRowsFromResponseData(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.sale_details)) return data.sale_details;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.sales)) return data.sales;
  if (Array.isArray(data.records)) return data.records;
  return [];
}

/** Normalize status for Invoice "Active" filter (1 = open/finalized sale, 3 = return). */
export function normalizeInvoiceTableStatus(status) {
  if (status === null || status === undefined || status === "") return "1";
  if (status === 1 || status === "1") return "1";
  if (status === 3 || status === "3") return "3";
  const u = String(status).toUpperCase();
  if (u === "ACTIVE" || u === "OPEN" || u === "PENDING" || u === "DRAFT") {
    return "1";
  }
  // ERP list often uses FINAL / POSTED — same as legacy "1" for Active filter
  if (
    u === "FINAL" ||
    u === "COMPLETED" ||
    u === "POSTED" ||
    u === "CONFIRMED" ||
    u === "SETTLED"
  ) {
    return "1";
  }
  if (
    u === "RETURN" ||
    u === "RETURNED" ||
    u === "CANCELLED" ||
    u === "CANCELED" ||
    u === "VOID"
  ) {
    return "3";
  }
  return String(status);
}

/** Creator user id from sale list/show payload (`created_by` / `sales_user_admin_id`). */
export function resolveSaleByUserId(invoice = {}) {
  const raw =
    invoice.by_user ??
    invoice.created_by ??
    invoice.sales_user_admin_id ??
    invoice.salesUserAdminId ??
    invoice.admin_id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Creator display name from sale list/show payload. */
export function resolveSaleByUserName(invoice = {}) {
  const named =
    invoice.by_user_name ?? invoice.created_by_name ?? invoice.byUserName;
  if (named != null && String(named).trim() !== "") {
    return String(named).trim();
  }
  return "N/A";
}

/** Map one API/socket sale object to Invoice table row (shared list + realtime). */
export function mapSaleDetailToInvoiceRow(invoice) {
  if (!invoice || invoice.id == null) return null;
  const grand = num(invoice.grand_total, 0);
  let remaining;
  if (
    invoice.remaining_amount != null &&
    invoice.remaining_amount !== ""
  ) {
    remaining = String(num(invoice.remaining_amount, 0).toFixed(4));
  } else if (
    invoice.remainingAmount != null &&
    invoice.remainingAmount !== ""
  ) {
    remaining = String(num(invoice.remainingAmount, 0).toFixed(4));
  } else {
    const paidFromPayments = sumPaymentAmounts(invoice.payments);
    const paid =
      invoice.paid_amount != null && invoice.paid_amount !== ""
        ? num(invoice.paid_amount, 0)
        : paidFromPayments;
    remaining = String(Math.max(0, grand - paid).toFixed(4));
  }
  const paidAmount =
    invoice.paid_amount != null && invoice.paid_amount !== ""
      ? num(invoice.paid_amount, 0)
      : sumPaymentAmounts(invoice.payments);
  const totalItems =
    invoice.item_count ??
    (Array.isArray(invoice.items) ? invoice.items.length : 0) ??
    (Array.isArray(invoice.sales) ? invoice.sales.length : 0);

  return {
    id: invoice.id,
    voucherNumber:
      invoice.doc_number || invoice.voucher_number || String(invoice.id),
    userId: invoice.customer_id ?? invoice.user_id ?? "0",
    customerName: invoice.customer_name || invoice.name || "N/A",
    customerMobile: invoice.customer_mobile || invoice.mobile || "N/A",
    byUser: resolveSaleByUserId(invoice),
    byUserName: resolveSaleByUserName(invoice),
    totalItems,
    grandTotal: invoice.grand_total || "0.00",
    final_amount:
      invoice.total_amount || invoice.final_amount || invoice.grand_total || "0.00",
    remainingAmount: remaining,
    paid_amount: paidAmount,
    paidAmount: String(paidAmount.toFixed(4)),
    status: normalizeInvoiceTableStatus(invoice.status),
    doc_date: invoice.doc_date || invoice.date || "",
    created_at: invoice.created_at || "",
    creationDate:
      invoice.doc_date ||
      invoice.date ||
      invoice.created_at ||
      new Date().toISOString().split("T")[0],
    round_off: invoice.round_off || "0.00",
    note: invoice.note || invoice.notes || "",
    items: Array.isArray(invoice.items)
      ? invoice.items
      : Array.isArray(invoice.sales)
        ? invoice.sales
        : [],
    payments: Array.isArray(invoice.payments) ? invoice.payments : [],
    followup_date: invoice.followup_date || "",
    discount: invoice.discount || "0.00",
    discount_amount: invoice.discount_amount || "0.00",
    gst_percent: invoice.gst_percent || "0.00",
    gst_amount: invoice.gst_amount || invoice.tax_amount || "0.00",
    sgst: invoice.sgst || "0.00",
    cgst: invoice.cgst || "0.00",
    igst: invoice.igst || "0.00",
    repairing_id: invoice.repairing_id || 0,
  };
}

/** Normalize list endpoint rows for Invoice table. */
export function normalizeListSalesRows(res) {
  if (!isSalesApiSuccess(res) || res.data == null) return [];
  const rows = extractSalesListRowsFromResponseData(res.data);
  return rows
    .map((row) => mapSaleDetailToInvoiceRow(row))
    .filter(Boolean);
}

/**
 * Map a raw socket/API sale payload (new or legacy) to one Invoice table row.
 */
export function mapSocketSaleToInvoiceRow(raw) {
  if (!raw) return null;
  const legacy = newShowRowToLegacySaleDetails(raw);
  return mapSaleDetailToInvoiceRow(legacy || raw);
}

export function createSalesIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Map API `payment_mode` → UI pay_mode key. */
export function normalizeErpPaymentPayMode(payment) {
  return parsePaymentModeFromApi(payment?.payment_mode);
}
